import { gql } from "@urql/core";

// Queries
export const PURCHASE_REQUEST_LIST_QUERY = gql`
  query PurchaseRequestList(
    $sorting: [SortInput!]
    $paging: PaginationInput
  ) {
    purchaseRequests(query: { sorting: $sorting, paging: $paging }) {
      nodes {
        id
        requestNo
        templateId
        teamId
        dateRequested
        dateRequired
        totalAmount
        status
        isUnplanned
        attachments {
          originalName
          fileId
          mimeType
          size
        }
        approvals {
          order
          roleSlug
          stepName
          approvedBy
          approvedOn
          remarks
          stepStatus
        }
        budgetValidation {
          validatedAt
          validatedBy
          remarks
          totalAmount
        }
        approvalType
        externalWorkflow
        flowCaseId
        flowCaseNumber
        submittedBy
        assignedTo
        assignedBy
        assignedOn
        createdAt
        updatedAt
      }
    }
  }
`;

export const PURCHASE_REQUEST_LIST_APPROVED_QUERY = gql`
  query PurchaseRequestListApproved($filter: JSONObject) {
    purchaseRequests(query: { filter: $filter }) {
      nodes {
        id
        title
        description
        requestNo
        templateId
        teamId
        dateRequested
        dateRequired
        budgetSource
        budgetId
        totalAmount
        status
        isUnplanned
        attachments {
          originalName
          fileId
          mimeType
          size
        }
        tags
        notes
      items {
        id
        name
        description
        quantity
        requestedQty
        inSourcingQty
        awardedQty
        unitPrice
        totalPrice
        supplier
        unit
        specifications
        planRef {
          planId
          itemId
        }
        tags
      }
      vendor {
          vendorId
          companyName
          contactPerson
          email
          phone
          address
          tin
        }
        currency
        paymentTerms
        shippingInfo {
          method
          carrierType
          carrierName
          trackingNumber
          shippingAddress
          shippingCost
          specialInstructions
        }
        termsAndConditions
      }
    }
  }
`;

export const PURCHASE_REQUEST_ONE_QUERY = gql`
  query PurchaseRequestOne($id: String!) {
    purchaseRequest(id: $id) {
      id
      title
      description
      requestNo
      teamId
      templateId
      dateRequested
      dateRequired
      budgetSource
      budgetId
      totalAmount
      status
      isUnplanned
      attachments {
        originalName
        fileId
        mimeType
        size
      }
      tags
      notes
      items {
        id
        name
        description
        quantity
        requestedQty
        inSourcingQty
        awardedQty
        unitPrice
        totalPrice
        supplier
        planRef {
          planId
          itemId
        }
        tags
      }
      approvals {
        order
        roleSlug
        stepName
        approvedBy
        approvedOn
        remarks
        stepStatus
      }
      budgetValidation {
        validatedAt
        validatedBy
        remarks
        totalAmount
      }
      approvalType

      externalWorkflow
      flowCaseId
        flowCaseNumber
      submittedBy
      assignedTo
      assignedBy
      assignedOn
      createdAt
      updatedAt
    }
  }
`;

// Mutations
export const CREATE_PURCHASE_REQUEST_MUTATION = gql`
  mutation CreatePurchaseRequest($input: CreatePurchaseRequestInput!) {
    createPurchaseRequest(input: $input) {
      id
      requestNo
      title
      description
      status
      totalAmount
      teamId
      approvalType
      steps {
        order
        roleSlug
        stepName
      }
      externalWorkflow
      flowCaseId
        flowCaseNumber
      submittedBy
    }
  }
`;

export const UPDATE_PURCHASE_REQUEST_MUTATION = gql`
  mutation UpdatePurchaseRequest(
    $id: String!
    $input: UpdatePurchaseRequestInput!
  ) {
    updatePurchaseRequest(id: $id, input: $input) {
      id
      requestNo
      title
      description
      status
      totalAmount
      teamId
      items {
        id
        name
        description
        quantity
        requestedQty
        inSourcingQty
        awardedQty
        unitPrice
        totalPrice
        supplier
        planRef {
          planId
          itemId
        }
        tags
      }

      tags
      notes
      approvalType

      externalWorkflow
      flowCaseId
        flowCaseNumber
      submittedBy
      updatedAt
    }
  }
`;

export const DELETE_PURCHASE_REQUEST_MUTATION = gql`
  mutation DeletePurchaseRequest($id: String!) {
    deletePurchaseRequest(id: $id) {
      id
    }
  }
`;

export const SUBMIT_PURCHASE_REQUEST_MUTATION = gql`
  mutation SubmitPurchaseRequest($input: SubmitPurchaseRequestInput!) {
    submitPurchaseRequest(input: $input) {
      id
      requestNo
      status
      approvals {
        order
        roleSlug
        stepName
        stepStatus
      }
      totalAmount
      approvalType

      externalWorkflow
      flowCaseId
        flowCaseNumber
      submittedBy
    }
  }
`;

export const VALIDATE_PURCHASE_REQUEST_BUDGET_MUTATION = gql`
  mutation ValidatePurchaseRequestBudget(
    $input: ValidatePurchaseRequestBudgetInput!
  ) {
    validatePurchaseRequestBudget(input: $input) {
      id
      status
      budgetValidation {
        validatedAt
        validatedBy
        remarks
        totalAmount
      }
    }
  }
`;

export const APPROVE_PURCHASE_REQUEST_MUTATION = gql`
  mutation ApprovePurchaseRequest($input: StepApprovalInput!) {
    approvePurchaseRequest(input: $input) {
      id
      status
      approvals {
        order
        roleSlug
        stepStatus
        approvedBy
        approvedOn
        remarks
      }
    }
  }
`;

export const APPROVE_PURCHASE_REQUEST_DIRECT_MUTATION = gql`
  mutation ApprovePurchaseRequestDirect($requestId: String!, $remarks: String) {
    approvePurchaseRequestDirect(requestId: $requestId, remarks: $remarks) {
      id
      status
      approvals {
        order
        roleSlug
        stepName
        stepStatus
        approvedBy
        approvedOn
        remarks
      }
    }
  }
`;

export const REJECT_PURCHASE_REQUEST_MUTATION = gql`
  mutation RejectPurchaseRequest($input: StepApprovalInput!) {
    rejectPurchaseRequest(input: $input) {
      id
      status
      approvals {
        order
        roleSlug
        stepStatus
        approvedBy
        approvedOn
        remarks
      }
    }
  }
`;

export const ATTACH_PURCHASE_REQUEST_FILES_MUTATION = gql`
  mutation AttachPurchaseRequestFiles($input: AttachFilesInput!) {
    attachPurchaseRequestFiles(input: $input) {
      id
      attachments
    }
  }
`;

export const REMOVE_PURCHASE_REQUEST_FILE_MUTATION = gql`
  mutation RemovePurchaseRequestFile($input: RemoveFileInput!) {
    removePurchaseRequestFile(input: $input) {
      id
      attachments
    }
  }
`;

export const ASSIGN_PURCHASE_REQUEST_MUTATION = gql`
  mutation AssignPurchaseRequest($input: AssignPurchaseRequestInput!) {
    assignPurchaseRequest(input: $input) {
      id
      assignedTo
      assignedBy
      assignedOn
      status
    }
  }
`;

export const ASSIGNABLE_PURCHASE_REQUESTS_QUERY = gql`
  query AssignablePurchaseRequests {
    assignablePurchaseRequests {
      id
      requestNo
      title
      description
      totalAmount
      status
      assignedTo
      assignedBy
      assignedOn
      items {
        id
        name
        description
        quantity
        requestedQty
        inSourcingQty
        awardedQty
        unit
        unitPrice
        totalPrice
        specifications
        tags
      }
      currency
      attachments {
        fileId
        originalName
        mimeType
        size
      }
    }
  }
`;

// Autocomplete from approved plan items
export const APPROVED_PLAN_ITEMS_QUERY = gql`
  query ApprovedPlanItems(
    $teamId: String!
    $fiscalYear: Float!
    $search: String
    $limit: Float
  ) {
    approvedPlanItems(
      teamId: $teamId
      fiscalYear: $fiscalYear
      search: $search
      limit: $limit
    ) {
      itemId
      planId
      productName
      productDescription
      unitPrice
      supplier
    }
  }
`;
